import { config } from '../config/environment'
import {
  ONETAG_CONVERSIONS,
  type OneTagConvType,
  resolveOneTagRouteProduct,
} from '../config/oneTagEvents'

/**
 * Enliple OneTag — SPA + 비쇼핑 피드 (CallMtm)
 *
 * @see https://enliple.notion.site/SPA-f03f16dbd0d14139ac71f3bfd7037c79
 * @see https://enliple.notion.site/0f60fb7a8bb648b38560359526225276
 *
 * SPA: head에 tcs.js 1회 로드 + 라우터 변경마다 CallMtm() 호출
 * (매 페이지에 스크립트를 다시 넣지 않으므로 bare CallMtm()이 필수)
 *
 * 데이터매니저 페이지 이동: 화면 전환마다 callDM()
 * @see https://enliple.notion.site/SPA-Single-Page-Application-SNS-1b9907ce77c78127a6a3d5deba68dc05
 */

type CallMtmPayload = {
  productCode?: string
  cartBtn?: string
  productName?: string
  convType?: OneTagConvType
  click?: string
}

type CallMtmFn = ((payload?: CallMtmPayload) => void) & {
  q?: unknown[]
}

type CallDMFn = ((...args: unknown[]) => void) & {
  q?: unknown[]
}

declare global {
  interface Window {
    CallMtm?: CallMtmFn
    callDM?: CallDMFn
  }
}

let clickBindingsInstalled = false

function isOneTagEnabled(): boolean {
  return config.oneTag.enabled && typeof window !== 'undefined'
}

/** 스크립트 로드 전에도 큐에 쌓이도록 CallMtm stub 보장 */
export function ensureCallMtm(): CallMtmFn | null {
  if (typeof window === 'undefined') return null

  if (!window.CallMtm) {
    // SPA 가이드와 동일한 큐잉 패턴
    window.CallMtm = function CallMtmQueued(this: unknown) {
      const fn = window.CallMtm as CallMtmFn
      fn.q = fn.q || []
      // eslint-disable-next-line prefer-rest-params
      fn.q.push(arguments)
    } as CallMtmFn
  }

  return window.CallMtm
}

/**
 * 스크립트 로드 전에도 큐에 쌓이도록 callDM stub 보장.
 * 가이드 스니펫과 동일: window['callDM'] = window['callDM'] || function () { ...queue... }
 */
export function ensureCallDM(): CallDMFn | null {
  if (typeof window === 'undefined') return null

  window.callDM =
    window.callDM ||
    function callDMQueued() {
      const fn = window.callDM as CallDMFn
      fn.q = fn.q || []
      // eslint-disable-next-line prefer-rest-params
      fn.q.push(arguments)
    }

  return window.callDM
}

/**
 * 화면 전환 시 페이지 이동 데이터 수집.
 * HTML에 callDM()을 다시 넣지 않고, SPA 라우터 변경마다 1회 호출한다.
 */
export function trackCallDMPageView(): void {
  if (!isOneTagEnabled()) return
  const callDM = ensureCallDM()
  if (!callDM) return
  callDM()
}

/**
 * CallMtm 호출.
 * - 인자 없음: SPA 라우터 변경 후 공통 수집 (대시보드 pageLoad 커스텀 이벤트)
 * - payload: 비쇼핑 상품/전환 수동 수집
 */
function invokeCallMtm(payload?: CallMtmPayload): void {
  if (!isOneTagEnabled()) return
  const callMtm = ensureCallMtm()
  if (!callMtm) return
  if (payload === undefined) {
    callMtm()
    return
  }
  callMtm(payload)
}

/** SPA: 라우터 변경 후 bare CallMtm() — 원태그/모비온 공통 수집 */
export function trackOneTagSpaPageView(): void {
  invokeCallMtm()
}

/** 비쇼핑 상품(페이지) 수집 */
export function trackOneTagProduct(productCode: string, cartBtn = ''): void {
  if (!productCode) return
  invokeCallMtm({ productCode, cartBtn })
}

/** 비쇼핑 전환 */
export function trackOneTagConversion(
  productName: string,
  convType: OneTagConvType = 'etc'
): void {
  if (!productName) return
  invokeCallMtm({ productName, convType })
}

/** 클릭 시 전환 — CSS selector 바인딩 */
export function bindOneTagClickConversion(
  productName: string,
  convType: OneTagConvType,
  clickSelector: string
): void {
  if (!productName || !clickSelector) return
  invokeCallMtm({ productName, convType, click: clickSelector })
}

/**
 * SPA 라우트 변경 시 1회 호출.
 * - 매핑된 메뉴 경로: 비쇼핑 productCode 포함 CallMtm (공통 수집 + 상품 식별)
 * - 그 외: bare CallMtm() 만 (중복 호출 방지 — SPA 가이드)
 */
export function trackOneTagRoute(barePath: string): void {
  const product = resolveOneTagRouteProduct(barePath)
  if (product) {
    trackOneTagProduct(product.productCode)
    return
  }
  trackOneTagSpaPageView()
}

/** 책 만들기 (/book-creation) 전환 */
export function trackOneTagBookCreation(): void {
  const { productName, convType } = ONETAG_CONVERSIONS.bookCreation
  trackOneTagConversion(productName, convType)
}

/**
 * iOS / Android 스토어 배지 클릭 전환 바인딩.
 * DOM에 배지가 생긴 뒤(홈 히어로 마운트) 호출.
 */
export function initOneTagClickConversions(force = false): void {
  if (!isOneTagEnabled()) return
  if (clickBindingsInstalled && !force) return
  clickBindingsInstalled = true
  ensureCallMtm()

  const ios = ONETAG_CONVERSIONS.appDownloadIos
  const android = ONETAG_CONVERSIONS.appDownloadAndroid

  if (ios.clickSelector) {
    bindOneTagClickConversion(ios.productName, ios.convType, ios.clickSelector)
  }
  if (android.clickSelector) {
    bindOneTagClickConversion(
      android.productName,
      android.convType,
      android.clickSelector
    )
  }
}

/** 스토어 배지 직접 클릭 시 폴백 전환 */
export function trackOneTagAppDownload(platform: 'ios' | 'android'): void {
  const event =
    platform === 'ios'
      ? ONETAG_CONVERSIONS.appDownloadIos
      : ONETAG_CONVERSIONS.appDownloadAndroid
  trackOneTagConversion(event.productName, event.convType)
}

export function getOneTagEid(): string {
  return config.oneTag.eid
}
